import React, { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import {
  CheckCircle2, XCircle, AlertTriangle, Loader2, Lock,
  Shield, Send, RotateCcw, Clock, History, User, ChevronDown, FileText,
} from 'lucide-react';
import axiosInstance from '../../api/axios';
import { resolve403ErrorMessage } from '../../request/getApiErrorMessage.ts';
import { MODULE_KEYS } from '../../constants/moduleAccess.ts';
import { useRequireModuleAccess } from '../../hooks/useRequireModuleAccess.ts';
import ModuleAccessDeniedDialog from '../shared/ModuleAccessDeniedDialog.tsx';
import useAuthUser from '../../hooks/useAuthUser';

/**
 * SignOffPanel — multi-step reviewer sign-off workflow.
 *
 * Workflow: draft / in_progress / completed / in_review
 *              → [Submit for Review] → prepared
 *              → [Sign Off] → signed_off   (locked)
 *              → [Send Back] → in_review   (preparer revises and resubmits)
 *
 * Gates (both submit-for-review and sign-off):
 *   - No open non-false-positive exceptions.
 * Sign-off also:
 *   - Requires conclusion.
 *   - Preparer ≠ reviewer (SOD): typed names differ, and the logged-in user is neither the run
 *     creator nor the preparer (run.viewer_is_creator / viewer_is_preparer, set by the backend).
 */
export default function SignOffPanel({ run, onSigned, refreshKey = 0 }) {
  const runId = run?.id;
  const status = run?.workflow_status || 'draft';
  const isLocked = status === 'signed_off';

  const { checkAccess, dialogProps } = useRequireModuleAccess(MODULE_KEYS.TEST_EXECUTIONS, {
    hint: 'Signing off a test run requires the Test Executions module.',
  });

  const [exceptions, setExceptions] = useState([]);
  const [excLoading, setExcLoading] = useState(true);

  const fetchExceptions = useCallback(async () => {
    if (!runId) return;
    setExcLoading(true);
    try {
      const resp = await axiosInstance.get(`/api/control-test-runs/${runId}/exceptions`);
      setExceptions(resp.data?.exceptions || []);
    } catch {
      setExceptions([]);
    } finally {
      setExcLoading(false);
    }
  }, [runId]);

  useEffect(() => { fetchExceptions(); }, [fetchExceptions, refreshKey]);

  // Open exceptions that block the gate (false-positives are excluded).
  const openBlocking = exceptions.filter(e => e.disposition === 'open' && !e.false_positive);
  const gateBlocked  = openBlocking.length > 0;

  if (isLocked) return <SignedOffView run={run} />;

  if (status === 'prepared') {
    return (
      <ReviewerPanel
        run={run}
        runId={runId}
        gateBlocked={gateBlocked}
        openBlocking={openBlocking}
        excLoading={excLoading}
        checkAccess={checkAccess}
        dialogProps={dialogProps}
        onSigned={onSigned}
      />
    );
  }

  return (
    <PreparerPanel
      run={run}
      runId={runId}
      gateBlocked={gateBlocked}
      openBlocking={openBlocking}
      excLoading={excLoading}
      onSigned={onSigned}
    />
  );
}

/* ─── Preparer panel (draft / completed / in_review) ───────────────────────── */

function PreparerPanel({ run, runId, gateBlocked, openBlocking, excLoading, onSigned }) {
  const [preparerName, setPreparerName] = useState(run?.preparer_name || '');
  const [attested, setAttested]         = useState(false);
  const [saving, setSaving]             = useState(false);

  const wasSentBack = run?.workflow_status === 'in_review';

  const canSubmit = !gateBlocked && !saving && preparerName.trim() && attested;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSaving(true);
    try {
      await axiosInstance.patch(`/api/control-test-runs/${runId}/submit-for-review`, {
        preparer_name: preparerName.trim(),
      });
      toast.success('Submitted for review.');
      onSigned?.();
    } catch (err) {
      toast.error('Submit failed: ' + resolve403ErrorMessage(err, err.response?.data?.error || err.message));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-5">
      <div className="flex items-center gap-2">
        <Send className="h-4 w-4 text-gray-400" />
        <h4 className="text-sm font-semibold text-gray-800">Submit for Review</h4>
        <WorkflowStatusBadge status={run?.workflow_status} />
      </div>

      {/* Sent-back notice */}
      {wasSentBack && run?.review_notes && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 space-y-1">
          <p className="text-xs font-semibold text-amber-800 flex items-center gap-1.5">
            <RotateCcw className="h-3.5 w-3.5" />Sent back for revision
          </p>
          <p className="text-xs text-amber-700 italic">"{run.review_notes}"</p>
        </div>
      )}

      {/* Exception gate */}
      <ExceptionGate excLoading={excLoading} gateBlocked={gateBlocked} openBlocking={openBlocking} />

      {/* Preparer name */}
      <div>
        <p className="text-xs font-medium text-gray-600 mb-1">Preparer Name</p>
        <input
          value={preparerName}
          onChange={e => setPreparerName(e.target.value)}
          placeholder="Your name"
          disabled={gateBlocked}
          className="w-full px-3 py-2 border border-gray-200 rounded-lg text-xs focus:outline-none focus:ring-1 focus:ring-indigo-400 disabled:bg-gray-50"
        />
      </div>

      {/* Attestation */}
      <label className={`flex items-start gap-2.5 cursor-pointer ${gateBlocked ? 'opacity-40 pointer-events-none' : ''}`}>
        <input
          type="checkbox"
          checked={attested}
          onChange={e => setAttested(e.target.checked)}
          className="mt-0.5 rounded border-gray-300 text-indigo-600"
        />
        <span className="text-xs text-gray-700 leading-relaxed">
          I confirm that all samples have been reviewed, all exceptions dispositioned,{' '}
          {run?.population_status === 'not_required'
            ? 'the evidence files used as samples are complete'
            : 'the population is confirmed'},
          and this workpaper is ready for reviewer sign-off.
        </span>
      </label>

      <button
        onClick={handleSubmit}
        disabled={!canSubmit}
        className="w-full py-2.5 text-sm font-semibold bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
      >
        {saving
          ? <><Loader2 className="h-4 w-4 animate-spin" />Submitting…</>
          : <><Send className="h-4 w-4" />Submit for Review</>
        }
      </button>
    </div>
  );
}

/* ─── Reviewer panel (prepared) ─────────────────────────────────────────────── */

function ReviewerPanel({ run, runId, gateBlocked, openBlocking, excLoading, checkAccess, dialogProps, onSigned }) {
  const authUser = useAuthUser();
  // The backend refuses sign-off from the run creator or the preparer, and send-back from the preparer.
  const viewerIsCreator  = !!run?.viewer_is_creator;
  const viewerIsPreparer = !!run?.viewer_is_preparer;
  const loginBlocked     = viewerIsCreator || viewerIsPreparer;

  const [conclusion, setConclusion] = useState(run?.conclusion || '');
  const [reviewer, setReviewer]     = useState(
    run?.reviewer_name || (loginBlocked ? '' : authUser?.username || ''),
  );
  const [notes, setNotes]           = useState('');
  const [saving, setSaving]         = useState(false);
  const [sendingBack, setSendingBack] = useState(false);
  const [showSendBack, setShowSendBack] = useState(false);
  const [sendBackNotes, setSendBackNotes] = useState('');

  const preparerName = run?.preparer_name || '';
  const preparedAt   = run?.prepared_at;

  const namesDiffer = reviewer.trim() && preparerName.trim() &&
    reviewer.trim().toLowerCase() !== preparerName.trim().toLowerCase();
  const segregationWarn = reviewer.trim() && preparerName.trim() && !namesDiffer;
  const segregationOk   = namesDiffer && !loginBlocked;

  const loginRole = viewerIsCreator && viewerIsPreparer ? 'created and prepared'
    : viewerIsCreator ? 'created' : 'prepared';

  const canSignOff = !gateBlocked && !saving && conclusion && segregationOk;

  const handleSignOff = async () => {
    if (!canSignOff) return;
    if (!checkAccess()) return;
    setSaving(true);
    try {
      await axiosInstance.patch(`/api/control-test-runs/${runId}/sign-off`, {
        conclusion,
        preparer: preparerName,
        reviewer: reviewer.trim(),
        review_notes: notes,
      });
      toast.success('Run signed off.');
      onSigned?.();
    } catch (err) {
      toast.error('Sign-off failed: ' + resolve403ErrorMessage(err, err.response?.data?.error || err.message));
    } finally {
      setSaving(false);
    }
  };

  const handleSendBack = async () => {
    if (!sendBackNotes.trim()) {
      toast.error('Please provide notes explaining what needs to be revised.');
      return;
    }
    setSendingBack(true);
    try {
      await axiosInstance.patch(`/api/control-test-runs/${runId}/send-back`, {
        review_notes: sendBackNotes.trim(),
      });
      toast.success('Sent back to preparer.');
      onSigned?.();
    } catch (err) {
      toast.error('Send back failed: ' + resolve403ErrorMessage(err, err.response?.data?.error || err.message));
    } finally {
      setSendingBack(false);
    }
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-5">
      <div className="flex items-center gap-2">
        <Shield className="h-4 w-4 text-gray-400" />
        <h4 className="text-sm font-semibold text-gray-800">Reviewer Sign-off</h4>
        <WorkflowStatusBadge status="prepared" />
      </div>

      {/* Preparer summary */}
      <div className="bg-indigo-50 border border-indigo-100 rounded-lg px-4 py-3 text-xs text-indigo-800 space-y-0.5">
        <p><span className="font-semibold">Prepared by:</span> {preparerName || '—'}</p>
        {preparedAt && (
          <p><span className="font-semibold">Submitted:</span> {new Date(preparedAt).toLocaleString()}</p>
        )}
      </div>

      {/* Exception gate */}
      <ExceptionGate excLoading={excLoading} gateBlocked={gateBlocked} openBlocking={openBlocking} />

      {/* Login-based SOD: the creator / preparer cannot sign off their own run */}
      {loginBlocked && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3">
          <p className="text-xs font-semibold text-amber-800 flex items-center gap-1.5">
            <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
            You {loginRole} this run. Sign in as the reviewer to sign off.
          </p>
        </div>
      )}

      {/* Conclusion */}
      <div>
        <p className="text-xs font-medium text-gray-600 mb-2">Conclusion</p>
        <div className="grid grid-cols-3 gap-2">
          {CONCLUSION_OPTIONS.map(o => {
            const Icon = o.icon;
            const active = conclusion === o.id;
            return (
              <button
                key={o.id}
                onClick={() => setConclusion(o.id)}
                disabled={gateBlocked}
                className={`flex flex-col items-center gap-1 p-3 rounded-lg border transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
                  active ? o.activeClass : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <Icon className={`h-4 w-4 ${active ? o.iconClass : 'text-gray-400'}`} />
                <span className="text-xs font-medium text-gray-700">{o.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Reviewer name */}
      <div>
        <p className="text-xs font-medium text-gray-600 mb-1">Reviewer Name</p>
        <input
          value={reviewer}
          onChange={e => setReviewer(e.target.value)}
          placeholder="Your name (must differ from preparer)"
          disabled={gateBlocked}
          className="w-full px-3 py-2 border border-gray-200 rounded-lg text-xs focus:outline-none focus:ring-1 focus:ring-indigo-400 disabled:bg-gray-50"
        />
        {segregationWarn && (
          <p className="text-xs text-red-600 mt-1 flex items-center gap-1">
            <AlertTriangle className="h-3 w-3" />Reviewer must be different from preparer (SOD).
          </p>
        )}
        {segregationOk && !gateBlocked && (
          <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
            <CheckCircle2 className="h-3 w-3" />Segregation of duties verified.
          </p>
        )}
      </div>

      {/* Review notes */}
      <div>
        <p className="text-xs font-medium text-gray-600 mb-1">Review Notes <span className="font-normal text-gray-400">(optional)</span></p>
        <textarea
          value={notes}
          onChange={e => setNotes(e.target.value)}
          rows={2}
          disabled={gateBlocked}
          placeholder="Any observations for the workpaper record"
          className="w-full px-3 py-2 border border-gray-200 rounded-lg text-xs resize-none focus:outline-none focus:ring-1 focus:ring-indigo-400 disabled:bg-gray-50"
        />
      </div>

      {/* Action buttons */}
      <div className="flex gap-3">
        <button
          onClick={() => setShowSendBack(s => !s)}
          disabled={viewerIsPreparer}
          title={viewerIsPreparer ? 'The preparer cannot send back their own submission' : undefined}
          className="flex-1 py-2.5 text-sm font-semibold border border-amber-300 text-amber-700 rounded-xl hover:bg-amber-50 disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
        >
          <RotateCcw className="h-4 w-4" />Send Back
        </button>
        <button
          onClick={handleSignOff}
          disabled={!canSignOff}
          className="flex-1 py-2.5 text-sm font-semibold bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
        >
          {saving
            ? <><Loader2 className="h-4 w-4 animate-spin" />Saving…</>
            : <><Shield className="h-4 w-4" />Sign Off</>
          }
        </button>
      </div>

      {/* Send-back inline form */}
      {showSendBack && !viewerIsPreparer && (
        <div className="border border-amber-200 rounded-lg bg-amber-50/40 px-4 py-3 space-y-2">
          <p className="text-xs font-semibold text-amber-800">Send back with notes</p>
          <textarea
            rows={3}
            value={sendBackNotes}
            onChange={e => setSendBackNotes(e.target.value)}
            placeholder="Explain what needs to be corrected or completed before you can sign off…"
            className="w-full text-xs border border-amber-300 rounded px-2.5 py-2 resize-none focus:outline-none focus:ring-1 focus:ring-amber-400 bg-white"
          />
          <div className="flex justify-end gap-2">
            <button onClick={() => setShowSendBack(false)} className="px-3 py-1.5 text-xs text-gray-500 border border-gray-200 rounded hover:bg-gray-50">Cancel</button>
            <button
              onClick={handleSendBack}
              disabled={sendingBack}
              className="px-3 py-1.5 text-xs font-medium text-white bg-amber-600 rounded hover:bg-amber-700 disabled:opacity-50 flex items-center gap-1"
            >
              {sendingBack && <Loader2 className="h-3 w-3 animate-spin" />}Send Back
            </button>
          </div>
        </div>
      )}

      <ModuleAccessDeniedDialog {...dialogProps} />

      {/* Audit log — helps the reviewer see what changed before signing off */}
      <AuditLogSection runId={runId} />
    </div>
  );
}

/* ─── Signed-off read-only view ─────────────────────────────────────────────── */

function SignedOffView({ run }) {
  return (
    <div className="space-y-4">
      <div className="bg-green-50 border border-green-200 rounded-xl p-5 space-y-3">
        <div className="flex items-center gap-3">
          <Shield className="h-5 w-5 text-green-600 shrink-0" />
          <p className="text-sm font-semibold text-green-800">Signed Off</p>
          <ConclusionBadge conclusion={run?.conclusion} />
        </div>
        <div className="text-xs text-green-700 space-y-0.5">
          {run?.preparer_name && <p>Prepared by: <strong>{run.preparer_name}</strong>{run.prepared_at && ` · ${new Date(run.prepared_at).toLocaleDateString()}`}</p>}
          {run?.reviewer_name && <p>Reviewed by: <strong>{run.reviewer_name}</strong>{run.signed_off_at && ` · ${new Date(run.signed_off_at).toLocaleDateString()}`}</p>}
          {run?.review_notes  && <p className="italic mt-1">"{run.review_notes}"</p>}
        </div>
        <div className="flex items-center gap-1.5 text-xs text-green-600 border-t border-green-200 pt-2">
          <Lock className="h-3.5 w-3.5" />
          This workpaper is locked. To make changes, create a new test run.
        </div>
      </div>
      <AuditLogSection runId={run?.id} />
    </div>
  );
}

/* ─── Exception gate (shared) ───────────────────────────────────────────────── */

function ExceptionGate({ excLoading, gateBlocked, openBlocking }) {
  if (excLoading) {
    return (
      <div className="flex items-center gap-2 text-gray-400 text-xs">
        <Loader2 className="h-3.5 w-3.5 animate-spin" />Checking exceptions…
      </div>
    );
  }
  if (gateBlocked) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3">
        <p className="text-xs font-semibold text-red-700 flex items-center gap-1.5">
          <XCircle className="h-3.5 w-3.5" />
          Blocked — {openBlocking.length} open exception{openBlocking.length !== 1 ? 's' : ''} must be resolved first
        </p>
        <ul className="mt-1.5 space-y-1">
          {openBlocking.map(e => (
            <li key={e.id} className="text-xs text-red-600 pl-5 break-words leading-relaxed">
              <span className="font-medium">{e.record_ref}{e.attribute_ref ? ` · Attr ${e.attribute_ref}` : ''}</span>
              {e.description ? ` — ${e.description}` : ''}
            </li>
          ))}
        </ul>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-1.5 text-xs text-green-600">
      <CheckCircle2 className="h-3.5 w-3.5" />No open exceptions — gate cleared
    </div>
  );
}

/* ─── Audit log timeline ─────────────────────────────────────────────────────── */

const ACTION_META = {
  CREATE: { label: 'Created',  cls: 'bg-green-100 text-green-700' },
  UPDATE: { label: 'Updated',  cls: 'bg-blue-100 text-blue-700' },
  DELETE: { label: 'Deleted',  cls: 'bg-red-100 text-red-700' },
};

function AuditLogSection({ runId }) {
  const [events, setEvents]     = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [collapsed, setCollapsed] = useState(false);

  const load = useCallback(async () => {
    if (!runId) return;
    setLoading(true);
    setError(null);
    try {
      const resp = await axiosInstance.get(`/api/control-test-runs/${runId}/audit-events`, {
        params: { page_size: 50 },
      });
      setEvents(resp.data?.events || []);
    } catch {
      setError('Failed to load audit log.');
    } finally {
      setLoading(false);
    }
  }, [runId]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      <button
        onClick={() => setCollapsed(v => !v)}
        className="w-full flex items-center gap-2 px-5 py-3 border-b border-gray-100 hover:bg-gray-50 transition-colors"
      >
        <History className="h-4 w-4 text-gray-400 shrink-0" />
        <h4 className="text-sm font-semibold text-gray-800">Audit Log</h4>
        {!loading && <span className="ml-1 text-[10px] text-gray-400">({events.length} events)</span>}
        <ChevronDown className={`h-4 w-4 text-gray-400 ml-auto transition-transform ${collapsed ? '' : 'rotate-180'}`} />
      </button>

      {!collapsed && (
        <div className="divide-y divide-gray-50">
          {loading ? (
            <div className="flex items-center gap-2 px-5 py-4 text-xs text-gray-400">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />Loading audit events…
            </div>
          ) : error ? (
            <p className="px-5 py-4 text-xs text-red-500">{error}</p>
          ) : events.length === 0 ? (
            <p className="px-5 py-4 text-xs text-gray-400 italic">No audit events recorded for this run yet.</p>
          ) : (
            events.map(ev => {
              const meta = ACTION_META[ev.action] || { label: ev.action, cls: 'bg-gray-100 text-gray-600' };
              const isOpen = expanded === ev.id;
              const ts = ev.created_at ? new Date(ev.created_at) : null;
              return (
                <div key={ev.id} className="px-5 py-3">
                  <button
                    onClick={() => setExpanded(isOpen ? null : ev.id)}
                    className="w-full text-left flex items-start gap-3"
                  >
                    <span className={`mt-0.5 shrink-0 text-[9px] font-bold px-1.5 py-0.5 rounded uppercase ${meta.cls}`}>
                      {meta.label}
                    </span>
                    <span className="flex-1 text-xs text-gray-700 leading-snug">
                      {ev.summary}
                    </span>
                    <span className="shrink-0 text-[10px] text-gray-400 whitespace-nowrap">
                      {ts ? ts.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'}
                    </span>
                  </button>

                  {/* Actor + expanded detail */}
                  <div className="mt-1 ml-11 flex items-center gap-2 flex-wrap">
                    {ev.actor_username && (
                      <span className="flex items-center gap-0.5 text-[10px] text-gray-400">
                        <User className="h-2.5 w-2.5" />{ev.actor_username}
                      </span>
                    )}
                    {ev.resource_label && (
                      <span className="flex items-center gap-0.5 text-[10px] text-gray-400">
                        <FileText className="h-2.5 w-2.5" />{ev.resource_label}
                      </span>
                    )}
                    {(ev.after_data || ev.before_data) && (
                      <button
                        onClick={() => setExpanded(isOpen ? null : ev.id)}
                        className="text-[10px] text-indigo-400 hover:text-indigo-600"
                      >
                        {isOpen ? 'Hide detail' : 'Show detail'}
                      </button>
                    )}
                  </div>

                  {isOpen && (ev.after_data || ev.before_data) && (
                    <div className="mt-2 ml-11 rounded bg-gray-50 border border-gray-200 p-2 text-[10px] font-mono text-gray-600 overflow-x-auto max-h-40 overflow-y-auto">
                      {ev.after_data
                        ? JSON.stringify(ev.after_data, null, 2)
                        : JSON.stringify(ev.before_data, null, 2)}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}

/* ─── Lock banner (exported for use in other tabs) ──────────────────────────── */

export function LockedRunBanner() {
  return (
    <div className="flex items-center gap-2 px-4 py-2.5 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-800 mb-4">
      <Lock className="h-3.5 w-3.5 shrink-0" />
      This workpaper is signed off and locked. No edits are permitted. To make changes, create a new test run.
    </div>
  );
}

/* ─── Helpers ────────────────────────────────────────────────────────────────── */

const CONCLUSION_OPTIONS = [
  { id: 'effective',    icon: CheckCircle2,  label: 'Effective',    activeClass: 'border-green-500 bg-green-50', iconClass: 'text-green-600' },
  { id: 'ineffective',  icon: XCircle,       label: 'Ineffective',  activeClass: 'border-red-500 bg-red-50',     iconClass: 'text-red-600' },
  { id: 'needs_review', icon: AlertTriangle, label: 'Needs Review', activeClass: 'border-amber-500 bg-amber-50', iconClass: 'text-amber-600' },
];

function WorkflowStatusBadge({ status }) {
  const meta = {
    draft:       { label: 'Draft',       cls: 'bg-gray-100 text-gray-600' },
    in_progress: { label: 'In Progress', cls: 'bg-blue-100 text-blue-700' },
    completed:   { label: 'Completed',   cls: 'bg-blue-100 text-blue-700' },
    prepared:    { label: 'Pending Review', cls: 'bg-indigo-100 text-indigo-700' },
    in_review:   { label: 'Needs Revision', cls: 'bg-amber-100 text-amber-700' },
    signed_off:  { label: 'Signed Off',  cls: 'bg-green-100 text-green-700' },
  };
  const m = meta[status] || meta.draft;
  return (
    <span className={`ml-auto text-[10px] font-semibold px-2 py-0.5 rounded-full ${m.cls}`}>
      {m.label}
    </span>
  );
}

function ConclusionBadge({ conclusion }) {
  if (!conclusion) return null;
  const tone =
    conclusion === 'effective'    ? 'bg-green-100 text-green-700'
    : conclusion === 'ineffective' ? 'bg-red-100 text-red-700'
    : 'bg-amber-100 text-amber-700';
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-medium capitalize ${tone}`}>
      {conclusion.replace(/_/g, ' ')}
    </span>
  );
}
